import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ApiURL from '../../controllers/Api';
import CompanyLayout from '../../crm/CompanyLayout';
import { toast } from '../../utils/toast';
import { formatKolkataDate, formatKolkataDateTime } from '../../utils/dateTime';
import { FiDownload, FiEye, FiFileText, FiTag, FiUsers, FiFilter } from 'react-icons/fi';
import { IndividualAnalytics } from './DashboardCharts';

const Dashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [userType, setUserType] = useState('individual');
    const [stats, setStats] = useState([]);
    const [recentApplications, setRecentApplications] = useState([]);
    const [recommendedJobs, setRecommendedJobs] = useState([]);
    const [upcomingInterviews, setUpcomingInterviews] = useState([]);
    const [profileStrength, setProfileStrength] = useState(0);

    // Company specific states
    const [allResumes, setAllResumes] = useState([]);
    const [filteredResumes, setFilteredResumes] = useState([]);
    const [displayedResumes, setDisplayedResumes] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [minAtsScoreFilter, setMinAtsScoreFilter] = useState('');
    const [experienceFilter, setExperienceFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortOrder, setSortOrder] = useState('newest');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [selectedResume, setSelectedResume] = useState(null);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignTargetResume, setAssignTargetResume] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [assignSaving, setAssignSaving] = useState(false);
    const [showTagModal, setShowTagModal] = useState(false);
    const [tagTargetResume, setTagTargetResume] = useState(null);
    const [subCompanies, setSubCompanies] = useState([]);
    const [selectedTagCompanyIds, setSelectedTagCompanyIds] = useState([]);
    const [selectedRequirementIds, setSelectedRequirementIds] = useState([]);
    const [selectedTagEmployeeIds, setSelectedTagEmployeeIds] = useState([]);
    const [tagNotes, setTagNotes] = useState('');
    const [tagSaving, setTagSaving] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [resumesPerPage, setResumesPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        checkUserType();
        fetchDashboardData();
    }, []);

    useEffect(() => {
        if (userType === 'company') {
            fetchVisibleResumes();
            fetchCompanyEmployees();
            fetchCompanySubCompanies();
        }
    }, [userType]);

    useEffect(() => {
        if (allResumes.length > 0) {
            extractCategories();
        }
    }, [allResumes]);

    useEffect(() => {
        if (!showTagModal) {
            return;
        }

        const availableRequirementIds = new Set(
            subCompanies
                .filter((company) => selectedTagCompanyIds.includes(String(company.id)))
                .flatMap((company) => (company.requirements || []).map((requirement) => String(requirement.id)))
        );

        setSelectedRequirementIds((prev) => prev.filter((id) => availableRequirementIds.has(String(id))));
    }, [showTagModal, subCompanies, selectedTagCompanyIds]);

    useEffect(() => {
        filterResumes();
    }, [selectedCategory, searchTerm, minAtsScoreFilter, experienceFilter, statusFilter, sortOrder, allResumes, sourceFilter]);

    useEffect(() => {
        const indexOfLastResume = currentPage * resumesPerPage;
        const indexOfFirstResume = indexOfLastResume - resumesPerPage;
        setDisplayedResumes(filteredResumes.slice(indexOfFirstResume, indexOfLastResume));
        setTotalPages(Math.ceil(filteredResumes.length / resumesPerPage));
    }, [filteredResumes, currentPage, resumesPerPage]);

    const checkUserType = () => {
        const type = localStorage.getItem('userType') || 'individual';
        setUserType(type);
    };

    const fetchDashboardData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const storedUser = localStorage.getItem('user');
            const parsedUser = storedUser ? JSON.parse(storedUser) : null;
            const storedUserType = parsedUser?.user_type || localStorage.getItem('userType') || 'individual';

            if (storedUserType === 'company') {
                setUserData(parsedUser);
                setUserType('company');
                return;
            }

            const response = await fetch(`${ApiURL}/dashboard.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setUserData(result.data.user);
                    setUserType(result.data.user.user_type || 'individual');

                    if (result.data.user.user_type === 'individual') {
                        setStats(result.data.stats || getDefaultIndividualStats());
                        setRecentApplications(result.data.recent_applications || []);
                        setRecommendedJobs(result.data.recommended_jobs || []);
                        setUpcomingInterviews(result.data.upcoming_interviews || []);
                        setProfileStrength(result.data.profile_strength || 45);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchVisibleResumes = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/get_visible_resumes.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();
            if (result.success) {
                const resumes = Array.isArray(result.data) ? result.data : [];
                setAllResumes(resumes);
                return resumes;
            }
        } catch (error) {
            console.error('Error fetching resumes:', error);
            setAllResumes([]);
        }
        return [];
    };

    const fetchCompanyEmployees = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}crm/admin/get_employees.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const result = await response.json().catch(() => ({}));
            if (response.ok && result.success) {
                setEmployees((result.data?.employees || []).filter((employee) => employee.status === 'active'));
            }
        } catch (error) {
            console.error('Error fetching company employees:', error);
            setEmployees([]);
        }
    };

    const fetchCompanySubCompanies = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}crm/company/get_sub_companies.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const result = await response.json().catch(() => ({}));
            if (response.ok && result.success) {
                setSubCompanies(result.data?.sub_companies || []);
            }
        } catch (error) {
            console.error('Error fetching sub companies:', error);
            setSubCompanies([]);
        }
    };

    const extractCategories = () => {
        const uniqueCategories = [...new Set(allResumes
            .map(r => r.extracted_category || r.category)
            .filter(cat => cat && cat !== '')
        )].sort();
        setCategories(uniqueCategories);
    };

    const handleExportCsv = async () => {
        try {
            const token = localStorage.getItem('token');
            
            // Get the filtered data to export
            const dataToExport = filteredResumes;
            
            if (dataToExport.length === 0) {
                toast.warning('No resumes to export');
                return;
            }

            // Prepare CSV data with all fields
            const csvRows = [];
            
            // CSV Headers
            const headers = [
                'ID',
                'File Name',
                'Candidate Name',
                'Email',
                'Phone',
                'Job Title',
                'Category',
                'Experience (Years)',
                'ATS Score',
                'Status',
                'Tagged Companies',
                'Requirements',
                'Requirement Notes',
                'Upload Date',
                'Uploaded By',
                'Uploader ID',
                'Source',
                'Resume Type',
                'Current/Archived',
                'Hired',
                'Hired Company',
                'Hired Requirement',
                'Hired Recruiter',
                'Hired Amount',
                'Extracted Skills',
                'Extracted Summary',
                'Extracted Education',
                'Extracted Certifications',
                'Extracted Location'
            ];
            csvRows.push(headers.join(','));

            // Data rows
            for (const resume of dataToExport) {
                const extracted = parseExtractedData(resume.extracted_data);
                
                const row = [
                    resume.id || '',
                    `"${(resume.original_filename || '').replace(/"/g, '""')}"`,
                    `"${(extracted?.full_name || resume.extracted_name || '').replace(/"/g, '""')}"`,
                    `"${(extracted?.email || resume.extracted_email || '').replace(/"/g, '""')}"`,
                    `"${(extracted?.phone || resume.extracted_phone || '').replace(/"/g, '""')}"`,
                    `"${(extracted?.job_title || resume.extracted_job_title || resume.job_title || '').replace(/"/g, '""')}"`,
                    `"${(extracted?.category || resume.extracted_category || resume.category || '').replace(/"/g, '""')}"`,
                    resume.experience_years || 0,
                    resume.ats_score || 0,
                    resume.is_current ? 'Current' : 'Archived',
                    `"${(resume.sub_company_names?.join(', ') || resume.sub_company_name || '').replace(/"/g, '""')}"`,
                    `"${(resume.requirement_names?.join(', ') || '').replace(/"/g, '""')}"`,
                    `"${(resume.requirement_notes || '').replace(/"/g, '""')}"`,
                    resume.upload_date || '',
                    `"${(resume.uploaded_by_name || '').replace(/"/g, '""')}"`,
                    `"${(resume.uploaded_by_display_id || resume.uploaded_by_id || '').toString().replace(/"/g, '""')}"`,
                    `"${(resume.resume_source_label || '').replace(/"/g, '""')}"`,
                    resume.is_portal_resume ? 'Portal' : 'Uploaded',
                    resume.is_current ? 'Current' : 'Archived',
                    resume.is_hired ? 'Yes' : 'No',
                    `"${(resume.hired_company_name || '').replace(/"/g, '""')}"`,
                    `"${(resume.hired_requirement_name || '').replace(/"/g, '""')}"`,
                    `"${(resume.hired_employee_name || '').replace(/"/g, '""')}"`,
                    resume.hired_amount ? `${resume.hired_amount} ${resume.hired_amount_unit || ''}` : '',
                    `"${(extracted?.skills?.join(', ') || '').replace(/"/g, '""')}"`,
                    `"${(extracted?.summary || '').replace(/"/g, '""')}"`,
                    `"${(extracted?.education?.map(e => typeof e === 'string' ? e : `${e.degree || e.course || ''} ${e.institution || ''} ${e.year || ''}`).join('; ') || '').replace(/"/g, '""')}"`,
                    `"${(extracted?.certifications?.map(c => typeof c === 'string' ? c : `${c.name || c.title || ''} ${c.issuer || ''}`).join('; ') || '').replace(/"/g, '""')}"`,
                    `"${(extracted?.location || '').replace(/"/g, '""')}"`
                ];
                csvRows.push(row.join(','));
            }

            // Create and download CSV file
            const csvString = csvRows.join('\n');
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `company_resumes_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success(`Exported ${dataToExport.length} resumes successfully`);
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('Failed to export CSV');
        }
    };

    const filterResumes = () => {
        let filtered = [...allResumes];

        // Filter by source
        if (sourceFilter === 'company') {
            const companyId = userData?.company_id || userData?.id;
            
            if (companyId) {
                filtered = filtered.filter(r => 
                    r.uploaded_by_company_id === companyId || 
                    r.company_id === companyId ||
                    (r.user_id === companyId && !r.is_portal_resume)
                );
            }
        } else if (sourceFilter === 'careerhub') {
            filtered = filtered.filter(r => r.is_portal_resume);
        }

        if (selectedCategory) {
            filtered = filtered.filter(r =>
                (r.extracted_category === selectedCategory) || (r.category === selectedCategory)
            );
        }

        if (searchTerm && filtered.length > 0) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter((r) => {
                const extracted = parseExtractedData(r.extracted_data) || {};
                let storedSkills = Array.isArray(r.skills) ? r.skills : [];
                if (!storedSkills.length && r.skills_json) {
                    try {
                        storedSkills = JSON.parse(r.skills_json) || [];
                    } catch (e) {
                        storedSkills = [];
                    }
                }
                const skillText = [
                    ...(Array.isArray(extracted.skills) ? extracted.skills : []),
                    ...storedSkills,
                ].join(', ');

                return [
                    r.extracted_name,
                    r.extracted_job_title,
                    r.extracted_email,
                    r.original_filename,
                    r.extracted_phone,
                    r.resume_source_label,
                    r.uploaded_by_name,
                    r.uploaded_by_display_id,
                    r.uploaded_by_email,
                    r.sub_company_name,
                    r.sub_company_names?.join(', '),
                    skillText,
                ]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(term));
            });
        }

        if (experienceFilter) {
            filtered = filtered.filter((r) => {
                const experience = Number(r.experience_years || 0);

                switch (experienceFilter) {
                    case '0-1':
                        return experience <= 1;
                    case '1-3':
                        return experience >= 1 && experience <= 3;
                    case '3-5':
                        return experience >= 3 && experience <= 5;
                    case '5+':
                        return experience >= 5;
                    default:
                        return true;
                }
            });
        }

        if (minAtsScoreFilter) {
            filtered = filtered.filter((r) => {
                const atsScore = Number(r.ats_score || 0);

                switch (minAtsScoreFilter) {
                    case '0-20':
                        return atsScore >= 0 && atsScore <= 20;
                    case '21-40':
                        return atsScore >= 21 && atsScore <= 40;
                    case '41-60':
                        return atsScore >= 41 && atsScore <= 60;
                    case '61-80':
                        return atsScore >= 61 && atsScore <= 80;
                    case '81-100':
                        return atsScore >= 81 && atsScore <= 100;
                    default:
                        return true;
                }
            });
        }

        if (statusFilter) {
            filtered = filtered.filter((r) => statusFilter === 'current' ? Boolean(r.is_current) : !r.is_current);
        }

        filtered.sort((a, b) => {
            const left = a.upload_date ? new Date(a.upload_date).getTime() : 0;
            const right = b.upload_date ? new Date(b.upload_date).getTime() : 0;

            return sortOrder === 'oldest' ? left - right : right - left;
        });

        setFilteredResumes(filtered);
        setCurrentPage(1);
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
    };

    const resetCompanyFilters = () => {
        setSelectedCategory('');
        setSearchTerm('');
        setMinAtsScoreFilter('');
        setExperienceFilter('');
        setStatusFilter('');
        setSortOrder('newest');
        setSourceFilter('all');
        setResumesPerPage(10);
    };

    const handleViewResume = async (resume) => {
        if (!resume) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/admin/view_resume.php?resume_id=${resume.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 100);
            }
        } catch (error) {
            console.error('Error viewing resume:', error);
            toast.error('Failed to view resume');
        }
    };

    const handleViewResumeDetails = (resume) => {
        setSelectedResume(resume);
        setShowResumeModal(true);
    };

    const handleDownloadResume = async (resume) => {
        if (!resume) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/admin/download_resume.php?resume_id=${resume.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = resume.original_filename || 'resume.pdf';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error downloading resume:', error);
            toast.error('Failed to download resume');
        }
    };

    const handleOpenAssignModal = async (resume) => {
        if (resume.is_hired) {
            toast.warning('This resume is already hired and locked');
            return;
        }
        const latestResumes = await fetchVisibleResumes();
        const latestResume = latestResumes.find((item) => item.id === resume.id) || resume;
        setAssignTargetResume(latestResume);
        setSelectedEmployeeIds((latestResume.assigned_employee_ids || []).map(String));
        setShowAssignModal(true);
        if (employees.length === 0) {
            await fetchCompanyEmployees();
        }
    };

    const handleAssignResume = async () => {
        if (!assignTargetResume) return;

        const employeeIds = selectedEmployeeIds.map(Number).filter(Boolean);
        if (employeeIds.length === 0) {
            toast.warning('Please select at least one recruiter');
            return;
        }

        setAssignSaving(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}crm/admin/assign_resume.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    resume_id: assignTargetResume.id,
                    employee_ids: employeeIds
                })
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok || result.success === false) {
                throw new Error(result.message || 'Failed to assign resume');
            }

            toast.success(result.message || 'Resume assigned successfully');
            setShowAssignModal(false);
            setAssignTargetResume(null);
            setSelectedEmployeeIds([]);
            await fetchVisibleResumes();
        } catch (error) {
            console.error('Error assigning resume:', error);
            toast.error(error.message || 'Failed to assign resume');
        } finally {
            setAssignSaving(false);
        }
    };

    const handleOpenTagModal = async (resume) => {
        if (resume.is_hired) {
            toast.warning('This resume is already hired and locked');
            return;
        }
        setTagTargetResume(resume);
        setSelectedTagCompanyIds((resume.sub_company_ids || []).map(String));
        setSelectedRequirementIds((resume.requirement_ids || []).map(String));
        setSelectedTagEmployeeIds((resume.assigned_employee_ids || []).map(String));
        setTagNotes(resume.requirement_notes || '');
        setShowTagModal(true);
        if (employees.length === 0) {
            await fetchCompanyEmployees();
        }
        if (subCompanies.length === 0) {
            await fetchCompanySubCompanies();
        }
    };

    const handleSaveTags = async () => {
        if (!tagTargetResume) return;

        setTagSaving(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}crm/company/save_resume_requirement.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    resume_id: tagTargetResume.id,
                    sub_company_ids: selectedTagCompanyIds.map(Number).filter(Boolean),
                    requirement_ids: selectedRequirementIds.map(Number).filter(Boolean),
                    requirement_notes: tagNotes
                })
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok || result.success === false) {
                throw new Error(result.message || 'Failed to save tags');
            }

            if (selectedTagEmployeeIds.length > 0) {
                const assignResponse = await fetch(`${ApiURL}crm/admin/assign_resume.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        resume_id: tagTargetResume.id,
                        employee_ids: selectedTagEmployeeIds.map(Number).filter(Boolean)
                    })
                });

                const assignResult = await assignResponse.json().catch(() => ({}));
                if (!assignResponse.ok || assignResult.success === false) {
                    throw new Error(assignResult.message || 'Tags saved but recruiter assignment failed');
                }
            }

            toast.success(selectedTagEmployeeIds.length > 0 ? 'Resume tags and recruiter assignment saved successfully' : (result.message || 'Resume tags saved successfully'));
            setShowTagModal(false);
            setTagTargetResume(null);
            setSelectedTagCompanyIds([]);
            setSelectedRequirementIds([]);
            setSelectedTagEmployeeIds([]);
            setTagNotes('');
            fetchVisibleResumes();
        } catch (error) {
            console.error('Error saving resume tags:', error);
            toast.error(error.message || 'Failed to save tags');
        } finally {
            setTagSaving(false);
        }
    };

    const getRequirementOptionsForSelectedCompanies = () => {
        if (!selectedTagCompanyIds.length) {
            return [];
        }

        return subCompanies
            .filter((company) => selectedTagCompanyIds.includes(String(company.id)))
            .flatMap((company) =>
                (company.requirements || []).map((requirement) => ({
                    ...requirement,
                    sub_company_id: company.id,
                    sub_company_name: company.name,
                }))
            );
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const goToPage = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getDefaultIndividualStats = () => [
        { icon: '📋', value: '12', label: 'Applications Sent', change: 15, color: 'bg-blue-500' },
        { icon: '👁️', value: '48', label: 'Profile Views', change: 23, color: 'bg-green-500' },
        { icon: '⭐', value: '8', label: 'Saved Jobs', change: -5, color: 'bg-yellow-500' },
        { icon: '🤝', value: '3', label: 'Interviews', change: 50, color: 'bg-purple-500' },
    ];

    const handleApplyJob = async (jobId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/apply_job.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ job_id: jobId })
            });

            const result = await response.json();
            if (result.success) {
                toast.success('Application submitted successfully!');
                fetchDashboardData();
            } else {
                toast.error(result.message || 'Failed to apply');
            }
        } catch (error) {
            console.error('Error applying to job:', error);
            toast.error('Failed to apply');
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'under review':
            case 'review':
                return 'bg-blue-100 text-blue-800 border border-blue-200';
            case 'interview':
            case 'interview scheduled':
                return 'bg-green-100 text-green-800 border border-green-200';
            case 'applied':
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 border border-red-200';
            case 'accepted':
            case 'hired':
                return 'bg-purple-100 text-purple-800 border border-purple-200';
            case 'shortlisted':
                return 'bg-indigo-100 text-indigo-800 border border-indigo-200';
            default:
                return 'bg-gray-100 text-gray-800 border border-gray-200';
        }
    };

    const formatHiredAmount = (amount, unit) => {
        if (amount === null || amount === undefined || amount === '') {
            return '-';
        }

        const lpaAmount = unit === 'per_month' ? Number(amount) * 12 / 100000 : Number(amount);
        return `₹${lpaAmount.toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })} LPA`;
    };

    const handleQuickAction = (action) => {
        switch (action) {
            case 'upload_resume':
                navigate('/profile');
                break;
            case 'update_profile':
                navigate('/profile');
                break;
            case 'view_jobs':
                navigate('/jobs');
                break;
            case 'network':
                navigate('/network');
                break;
            case 'job_alerts':
                navigate('/alerts');
                break;
            case 'view_calendar':
                window.open('https://calendar.google.com', '_blank');
                break;
        }
    };

    const formatDate = (dateString) => {
        return formatKolkataDate(dateString, { month: 'short', day: 'numeric' });
    };

    const getListPreview = (items = [], fallback = '-') => {
        const cleanItems = (items || []).filter(Boolean);
        if (!cleanItems.length) return fallback;
        return cleanItems.length === 1 ? cleanItems[0] : `${cleanItems[0]}...`;
    };

    const getFileIcon = (fileName) => {
        const ext = fileName?.split('.').pop().toLowerCase();
        if (ext === 'pdf') return '📕';
        if (ext === 'doc' || ext === 'docx') return '📘';
        return '📄';
    };

    const parseExtractedData = (extractedData) => {
        if (!extractedData) return null;
        if (typeof extractedData === 'string') {
            try {
                return JSON.parse(extractedData);
            } catch {
                return null;
            }
        }
        return extractedData;
    };

    const inferNameFromFilename = (fileName) => {
        if (!fileName) return '';
        const cleaned = fileName
            .replace(/\.[^.]+$/, '')
            .replace(/\[[^\]]*\]|\([^)]+\)/g, ' ')
            .replace(/\b(naukri|resume|cv|profile|candidate|latest|updated|final|copy|new)\b/gi, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/[_\-.]+/g, ' ')
            .replace(/\b\d+\s*(y|yr|yrs|year|years|m|mo|month|months)\b/gi, ' ')
            .replace(/\d+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        const words = cleaned.split(' ').filter(Boolean);
        if (words.length < 2 || words.length > 4) return '';
        return words
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const getCandidateName = (resume) =>
        resume?.extracted_name || resume?.extracted_data?.full_name || inferNameFromFilename(resume?.original_filename) || 'N/A';

    const getCleanLocation = (resume) => {
        const value = resume?.extracted_data?.location || resume?.location || '';
        if (/semantic kernel|foundry|docker|fastapi|python|sql|azure|machine learning|training|llm|rag|databricks/i.test(value)) {
            return '';
        }
        return value;
    };

    const inferExperienceYears = (resume) => {
        const text = [
            resume?.original_filename,
            resume?.extracted_data?.summary,
            ...(resume?.extracted_data?.experiences || []).map((item) =>
                typeof item === 'string' ? item : item?.description
            ),
        ]
            .filter(Boolean)
            .join(' ');

        const matches = [];
        const patterns = [
            /\b(\d{1,2})\s*y(?:ears?)?(?:[\s_,-]*(\d{1,2})\s*m(?:onths?)?)?\b/gi,
            /\b(?:over|more than|around|about|total experience)?\s*(\d{1,2})\s*\+?\s*(years|year|yrs|yr)\b/gi,
        ];

        patterns.forEach((pattern) => {
            let match;
            while ((match = pattern.exec(text)) !== null) {
                const years = Number(match[1] || 0);
                if (years > 0 && years < 60) {
                    matches.push(years);
                }
            }
        });

        return matches.length ? Math.max(...matches) : 0;
    };

    const getResumeExperienceYears = (resume) =>
        Math.max(
            Number(resume?.extracted_data?.experience_years || 0),
            Number(resume?.experience_years || 0),
            inferExperienceYears(resume)
        );

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 sm:h-14 sm:w-14 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    // INDIVIDUAL DASHBOARD
    if (userType === 'individual') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 lg:py-8">
                    <div className="max-w-7xl mx-auto">
                        {/* Welcome Header - Responsive */}
                        <div className="mb-4 sm:mb-6 lg:mb-8 bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5 md:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
                                <div className="flex items-center gap-3 sm:gap-4">
                                    <div className="relative flex-shrink-0">
                                        <img
                                            src={userData?.profile_image || `https://ui-avatars.com/api/?name=${userData?.first_name || 'User'}+${userData?.last_name || ''}&background=3B82F6&color=fff&size=150`}
                                            alt={userData?.first_name}
                                            className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-full object-cover border-4 border-white shadow-lg"
                                        />
                                        <div className="absolute bottom-0 right-0 w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 bg-green-500 rounded-full border-2 border-white"></div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 truncate">
                                            Welcome back, <span className="text-blue-600">{userData?.first_name || 'User'}!</span>
                                        </h1>
                                        <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-0.5 sm:mt-1 truncate">
                                            {userData?.headline || 'Ready to find your next opportunity?'}
                                        </p>
                                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 md:gap-3 mt-1.5 sm:mt-2 md:mt-3">
                                            {userData?.location && (
                                                <span className="flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                                                    <span>📍</span> {userData.location}
                                                </span>
                                            )}
                                            {userData?.current_position && (
                                                <span className="flex items-center gap-1 text-xs sm:text-sm text-gray-600">
                                                    <span>💼</span> {userData.current_position}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 sm:gap-3">
                                    <button
                                        onClick={() => navigate('/jobs')}
                                        className="flex-1 sm:flex-none px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
                                    >
                                        🔍 Find Jobs
                                    </button>
                                </div>
                            </div>

                            {/* Profile Strength Bar - Responsive */}
                            <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-100">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs sm:text-sm font-medium text-gray-700">Profile Strength</span>
                                    <span className="text-xs sm:text-sm font-bold text-blue-600">{profileStrength}%</span>
                                </div>
                                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500"
                                        style={{ width: `${profileStrength}%` }}
                                    ></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    Complete your profile to get better job matches
                                </p>
                            </div>
                        </div>

                        {/* Stats Grid - Responsive */}
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6 lg:mb-8">
                            {stats.map((stat, index) => (
                                <div key={index} className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-5 border border-gray-200 hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                                        <div className={`p-2 sm:p-3 rounded-lg ${stat.color} bg-opacity-10`}>
                                            <span className="text-lg sm:text-xl">{stat.icon}</span>
                                        </div>
                                        <span className={`text-xs font-semibold ${stat.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {stat.change > 0 ? '↑' : '↓'} {Math.abs(stat.change)}%
                                        </span>
                                    </div>
                                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{stat.value}</h3>
                                    <p className="text-xs sm:text-sm text-gray-600">{stat.label}</p>
                                </div>
                            ))}
                        </div>

                        {/* Analytics Charts */}
                        <IndividualAnalytics
                            applications={recentApplications}
                            recommendedJobs={recommendedJobs}
                            stats={stats}
                            profileStrength={profileStrength}
                        />

                        {/* Main Content - Responsive Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                            {/* Main Content - 2 columns on desktop */}
                            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                                {/* Recommended Jobs */}
                                <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden">
                                    <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gray-50">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-base sm:text-lg font-bold text-gray-900">Recommended for You</h2>
                                            <Link to="/jobs" className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium">
                                                View all →
                                            </Link>
                                        </div>
                                    </div>

                                    <div className="divide-y divide-gray-100">
                                        {recommendedJobs.length > 0 ? (
                                            recommendedJobs.slice(0, 3).map((job) => (
                                                <div key={job.id} className="p-4 sm:p-5 md:p-6 hover:bg-gray-50 transition-colors">
                                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                                                        <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                                                            <img
                                                                src={job.company_logo || `https://ui-avatars.com/api/?name=${job.company}&background=3B82F6&color=fff&size=100`}
                                                                alt={job.company}
                                                                className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate">{job.title}</h3>
                                                                <p className="text-gray-600 text-xs sm:text-sm mb-2 truncate">{job.company} • {job.location}</p>
                                                                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                                                    <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                                                                        {job.type || 'Full-time'}
                                                                    </span>
                                                                    {job.remote && (
                                                                        <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-green-50 text-green-700 text-xs rounded-full">
                                                                            Remote
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="flex flex-row sm:flex-col items-center justify-between sm:items-end gap-2 sm:gap-3">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-16 sm:w-20 h-1.5 sm:h-2 bg-gray-200 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-green-500 rounded-full"
                                                                        style={{ width: `${job.match_score || 85}%` }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-green-600 font-bold text-xs sm:text-sm">{job.match_score || 85}%</span>
                                                            </div>
                                                            <button
                                                                onClick={() => handleApplyJob(job.id)}
                                                                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                                                            >
                                                                Apply Now
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-6 sm:p-8 text-center">
                                                <div className="text-3xl sm:text-4xl mb-3">🔍</div>
                                                <p className="text-gray-600 text-sm sm:text-base">No recommended jobs yet</p>
                                                <button
                                                    onClick={() => navigate('/profile')}
                                                    className="mt-3 sm:mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                                >
                                                    Complete your profile →
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Recent Applications */}
                                <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden">
                                    <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gray-50">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-base sm:text-lg font-bold text-gray-900">Recent Applications</h2>
                                            <Link to="/applications" className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium">
                                                View all →
                                            </Link>
                                        </div>
                                    </div>

                                    <div className="divide-y divide-gray-100">
                                        {recentApplications.length > 0 ? (
                                            recentApplications.slice(0, 4).map((app) => (
                                                <div key={app.id} className="p-3 sm:p-4 hover:bg-gray-50">
                                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                                            <img
                                                                src={app.company_logo || `https://ui-avatars.com/api/?name=${app.company}&background=3B82F6&color=fff&size=100`}
                                                                alt={app.company}
                                                                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex-shrink-0"
                                                            />
                                                            <div className="min-w-0 flex-1">
                                                                <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{app.position}</h4>
                                                                <p className="text-xs sm:text-sm text-gray-600 truncate">{app.company}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 sm:gap-3">
                                                            <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatusBadgeClass(app.status)}`}>
                                                                {app.status}
                                                            </span>
                                                            <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(app.applied_date)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-6 sm:p-8 text-center">
                                                <div className="text-3xl sm:text-4xl mb-3">📄</div>
                                                <p className="text-gray-600 text-sm sm:text-base">No applications yet</p>
                                                <button
                                                    onClick={() => navigate('/jobs')}
                                                    className="mt-3 sm:mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                                >
                                                    Browse Jobs →
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar - Responsive */}
                            <div className="space-y-4 sm:space-y-6">
                                {/* Upcoming Interviews */}
                                <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 p-4 sm:p-5 md:p-6">
                                    <h3 className="font-bold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Upcoming Interviews</h3>
                                    {upcomingInterviews.length > 0 ? (
                                        <div className="space-y-3 sm:space-y-4">
                                            {upcomingInterviews.map((interview) => (
                                                <div key={interview.id} className="p-3 sm:p-4 bg-blue-50 rounded-lg">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="font-medium text-gray-900 text-sm sm:text-base">{interview.company}</span>
                                                        <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                                            {interview.type}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-gray-600 mb-2 truncate">{interview.position}</p>
                                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                                        <span className="text-xs text-gray-500">
                                                            {new Date(interview.time).toLocaleString()}
                                                        </span>
                                                        <button className="px-2 sm:px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">
                                                            Join
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-3 sm:py-4">
                                            <div className="text-2xl sm:text-3xl mb-2">📅</div>
                                            <p className="text-xs sm:text-sm text-gray-600">No upcoming interviews</p>
                                        </div>
                                    )}
                                </div>

                                {/* Quick Actions - Responsive Grid */}
                                <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 p-4 sm:p-5 md:p-6">
                                    <h3 className="font-bold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Quick Actions</h3>
                                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                        {[
                                            { icon: '📄', label: 'Upload Resume', action: 'upload_resume' },
                                            { icon: '👤', label: 'Update Profile', action: 'update_profile' },
                                            { icon: '🔍', label: 'Find Jobs', action: 'view_jobs' },
                                            { icon: '👥', label: 'Network', action: 'network' },
                                        ].map((item, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleQuickAction(item.action)}
                                                className="flex flex-col items-center p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                            >
                                                <span className="text-lg sm:text-xl mb-1">{item.icon}</span>
                                                <span className="text-xs font-medium text-gray-700 text-center">{item.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Profile Tips */}
                                <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6 text-white">
                                    <h3 className="font-bold mb-2 sm:mb-3 text-sm sm:text-base">Profile Tips ✨</h3>
                                    <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-blue-100">
                                        <li className="flex items-start gap-2">
                                            <span>•</span>
                                            <span>Add your skills to get better job matches</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span>•</span>
                                            <span>Upload a professional photo</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span>•</span>
                                            <span>Complete your work experience</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // COMPANY DASHBOARD - Table only, no grid/card view
    return (
        <CompanyLayout
            title="Resume Library"
            description="Browse the resume library available to your company and review candidate details."
        >
            <div className="w-full">
                {allResumes.length > 0 ? (
                    <>
                        {/* Export CSV and filters */}
                        <div className="mb-4 flex flex-wrap items-center gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleExportCsv}
                                className="rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium hover:bg-emerald-100 transition-colors flex items-center gap-1.5"
                            >
                                <span className="text-base sm:text-lg leading-none">📊</span>
                                Export CSV ({filteredResumes.length} resumes)
                            </button>
                            
                            <div className="relative">
                                <select
                                    value={sourceFilter}
                                    onChange={(e) => setSourceFilter(e.target.value)}
                                    className="appearance-none rounded-xl border border-gray-300 bg-white px-3 sm:px-4 py-2 pr-8 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="all">Showing: All Visible</option>
                                    <option value="company">Showing: My Uploads</option>
                                    <option value="careerhub">Showing: Talencar Resumes</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3 sm:p-4 shadow-sm">
                            <div className="flex flex-col gap-3">
                                {/* First row - Search and main filters */}
                                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="flex-1 min-w-[200px] rounded-xl border border-gray-300 px-3 py-2 text-sm"
                                        placeholder="Search file, name, email, phone, company, skill..."
                                    />

                                    <select
                                        value={selectedCategory}
                                        onChange={handleCategoryChange}
                                        className="flex-1 min-w-[130px] rounded-xl border border-gray-300 px-3 py-2 text-sm"
                                    >
                                        <option value="">All categories</option>
                                        {categories.map((category) => (
                                            <option key={category} value={category}>
                                                {category}
                                            </option>
                                        ))}
                                    </select>

                                    <select
                                        value={experienceFilter}
                                        onChange={(e) => setExperienceFilter(e.target.value)}
                                        className="flex-1 min-w-[120px] rounded-xl border border-gray-300 px-3 py-2 text-sm"
                                    >
                                        <option value="">All experience</option>
                                        <option value="0-1">0-1 years</option>
                                        <option value="1-3">1-3 years</option>
                                        <option value="3-5">3-5 years</option>
                                        <option value="5+">5+ years</option>
                                    </select>
                                </div>

                                {/* Second row - Additional filters and actions */}
                                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                    <select
                                        value={minAtsScoreFilter}
                                        onChange={(e) => setMinAtsScoreFilter(e.target.value)}
                                        className="flex-1 min-w-[120px] rounded-xl border border-gray-300 px-3 py-2 text-sm"
                                    >
                                        <option value="">All ATS scores</option>
                                        <option value="0-20">0-20</option>
                                        <option value="21-40">21-40</option>
                                        <option value="41-60">41-60</option>
                                        <option value="61-80">61-80</option>
                                        <option value="81-100">81-100</option>
                                    </select>

                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="flex-1 min-w-[110px] rounded-xl border border-gray-300 px-3 py-2 text-sm"
                                    >
                                        <option value="">All statuses</option>
                                        <option value="current">Current</option>
                                        <option value="archived">Archived</option>
                                    </select>

                                    <select
                                        value={sortOrder}
                                        onChange={(e) => setSortOrder(e.target.value)}
                                        className="flex-1 min-w-[110px] rounded-xl border border-gray-300 px-3 py-2 text-sm"
                                    >
                                        <option value="newest">Newest first</option>
                                        <option value="oldest">Oldest first</option>
                                    </select>

                                    <button
                                        type="button"
                                        onClick={resetCompanyFilters}
                                        className="rounded-xl border border-slate-300 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                        Reset
                                    </button>

                                    <div className="flex items-center gap-2 ml-auto">
                                        <span className="text-xs sm:text-sm text-slate-500 whitespace-nowrap">Show</span>
                                        <select
                                            value={resumesPerPage}
                                            onChange={(e) => {
                                                setResumesPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="w-auto rounded-xl border border-gray-300 px-2 sm:px-3 py-2 text-sm"
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <span className="text-xs sm:text-sm text-slate-500 whitespace-nowrap">per page</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {displayedResumes.length > 0 ? (
                            <>
                                {/* Table View Only */}
                                <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden mb-6 sm:mb-8">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">ID</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">File</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Candidate</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Email</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Phone</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Job Title</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Category</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Tagged Company</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Exp</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">ATS</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Uploaded</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Uploaded By</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {displayedResumes.map((resume) => {
                                                    const extracted = parseExtractedData(resume.extracted_data);
                                                    const displayName = extracted?.full_name || resume.extracted_name || inferNameFromFilename(resume.original_filename) || 'N/A';
                                                    const displayJobTitle = extracted?.job_title || resume.extracted_job_title || resume.job_title || 'N/A';
                                                    const displayEmail = extracted?.email || resume.extracted_email || 'N/A';
                                                    const displayPhone = extracted?.phone || resume.extracted_phone || 'N/A';
                                                    const displayCategory = extracted?.category || resume.extracted_category || resume.category || 'N/A';
                                                    
                                                    const currentCompanyId = userData?.company_id || userData?.id;
                                                    const isMyCompanyUpload = (!resume.is_portal_resume) && (resume.uploaded_by_company_id === currentCompanyId || resume.company_id === currentCompanyId || resume.user_id === currentCompanyId);
                                                    const displaySourceLabel = isMyCompanyUpload && resume.uploaded_by_name ? `Uploaded by ${resume.uploaded_by_name}` : resume.resume_source_label;

                                                    return (
                                                        <tr key={`table-${resume.id}`} className="hover:bg-gray-50">
                                                            <td className="px-3 py-2 text-gray-600 align-top">{resume.id}</td>
                                                            <td className="px-3 py-2 align-top">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-base">{getFileIcon(resume.original_filename)}</span>
                                                                    <span className="max-w-[160px] truncate text-gray-900" title={resume.original_filename}>
                                                                        {resume.original_filename}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-2 font-medium text-gray-900 align-top">{displayName}</td>
                                                            <td className="px-3 py-2 text-gray-600 align-top max-w-[170px] truncate" title={displayEmail}>{displayEmail}</td>
                                                            <td className="px-3 py-2 text-gray-600 align-top">{displayPhone}</td>
                                                            <td className="px-3 py-2 text-gray-900 align-top">{displayJobTitle}</td>
                                                            <td className="px-3 py-2 align-top">
                                                                <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded">
                                                                    {displayCategory}
                                                                </span>
                                                                {displaySourceLabel ? (
                                                                    <div className="mt-1">
                                                                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${resume.is_portal_resume ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-700'}`}>
                                                                            {displaySourceLabel}
                                                                        </span>
                                                                    </div>
                                                                ) : null}
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-600 align-top">
                                                                <div title={resume.sub_company_names?.join(", ") || resume.sub_company_name || "-"}>{getListPreview(resume.sub_company_names, resume.sub_company_name || "-")}</div>
                                                                {resume.requirement_names?.length ? (
                                                                    <div className="mt-1 text-[10px] text-gray-500" title={resume.requirement_names.join(", ")}>{getListPreview(resume.requirement_names)}</div>
                                                                ) : null}
                                                                {resume.requirement_notes && (
                                                                    <div className="mt-1 text-[10px] text-gray-400">{resume.requirement_notes}</div>
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-600 align-top">{getResumeExperienceYears({ ...resume, extracted_data: extracted }) || 0}y</td>
                                                            <td className="px-3 py-2 align-top whitespace-nowrap">
                                                                <div className="flex items-center gap-1">
                                                                    <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                                        <div
                                                                            className={`h-full rounded-full ${
                                                                                (resume.ats_score || 0) >= 80 ? 'bg-green-500' :
                                                                                (resume.ats_score || 0) >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                                                            }`}
                                                                            style={{ width: `${resume.ats_score || 0}%` }}
                                                                        ></div>
                                                                    </div>
                                                                    <span className={`text-[10px] font-medium ${
                                                                        (resume.ats_score || 0) >= 80 ? 'text-green-600' :
                                                                        (resume.ats_score || 0) >= 60 ? 'text-yellow-600' : 'text-red-600'
                                                                    }`}>
                                                                        {resume.ats_score || 0}%
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-2 text-gray-500 text-[10px] align-top whitespace-nowrap">{formatDate(resume.upload_date)}</td>
                                                            <td className="px-3 py-2 text-gray-600 align-top">
                                                                <div className="font-medium text-gray-900">{resume.uploaded_by_name || '-'}</div>
                                                                <div className="text-[10px] text-gray-500">{resume.uploaded_by_display_id || '-'}</div>
                                                            </td>
                                                            <td className="px-3 py-2 align-top whitespace-nowrap">
                                                                <div className="flex items-center gap-1">
                                                                    {resume.is_hired && (
                                                                        <span className="rounded-md bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-700">
                                                                            Hired
                                                                        </span>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleViewResume(resume)}
                                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100"
                                                                        title="View Resume"
                                                                    >
                                                                        <FiEye className="h-4 w-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleViewResumeDetails(resume)}
                                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100"
                                                                        title="Resume Details"
                                                                    >
                                                                        <FiFileText className="h-4 w-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleOpenTagModal(resume)}
                                                                        disabled={resume.is_hired}
                                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-amber-50"
                                                                        title={resume.is_hired ? 'Hired resumes are locked' : 'Tag Resume'}
                                                                    >
                                                                        <FiTag className="h-4 w-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleOpenAssignModal(resume)}
                                                                        disabled={resume.is_hired}
                                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-cyan-50 text-cyan-700 hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-cyan-50"
                                                                        title={resume.is_hired ? 'Hired resumes are locked' : 'Assign Recruiter'}
                                                                    >
                                                                        <FiUsers className="h-4 w-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDownloadResume(resume)}
                                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-green-50 text-green-700 hover:bg-green-100"
                                                                        title="Download Resume"
                                                                    >
                                                                        <FiDownload className="h-4 w-4" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-2 mt-6 sm:mt-8 mb-4">
                                        <button
                                            onClick={goToPrevPage}
                                            disabled={currentPage === 1}
                                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-sm ${
                                                currentPage === 1
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                            }`}
                                        >
                                            Previous
                                        </button>

                                        <div className="flex gap-1 overflow-x-auto max-w-full px-2">
                                            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 7) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 4) {
                                                    pageNum = i + 1;
                                                    if (i === 6) pageNum = totalPages;
                                                } else if (currentPage >= totalPages - 3) {
                                                    pageNum = totalPages - 6 + i;
                                                } else {
                                                    pageNum = currentPage - 3 + i;
                                                    if (i === 6) pageNum = totalPages;
                                                }
                                                
                                                if (pageNum === totalPages && i !== 6 && totalPages > 7 && currentPage <= totalPages - 4) {
                                                    if (i === 5) return <span key="dots" className="px-2 py-2">...</span>;
                                                    if (i < 5) return null;
                                                }
                                                
                                                if (pageNum > totalPages) return null;
                                                
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => goToPage(pageNum)}
                                                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-medium transition-colors text-sm ${
                                                            currentPage === pageNum
                                                                ? 'bg-blue-600 text-white'
                                                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <button
                                            onClick={goToNextPage}
                                            disabled={currentPage === totalPages}
                                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-sm ${
                                                currentPage === totalPages
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                            }`}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}

                                <div className="text-center text-xs sm:text-sm text-gray-500 mt-2">
                                    Showing {displayedResumes.length} of {filteredResumes.length} resumes
                                    {sourceFilter === 'company' && ' (Company Uploads Only)'}
                                    {sourceFilter === 'careerhub' && ' (Talencar Resumes Only)'}
                                </div>
                            </>
                        ) : (
                            <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 p-8 sm:p-12 text-center">
                                <div className="text-5xl sm:text-7xl mb-3 sm:mb-4">🔍</div>
                                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No Resumes Found</h3>
                                <p className="text-sm sm:text-base text-gray-600 px-2">
                                    {sourceFilter === 'company' 
                                        ? 'No resumes uploaded by your company match the filters.'
                                        : sourceFilter === 'careerhub'
                                            ? 'No Talencar resumes match the filters.'
                                            : searchTerm
                                            ? `No resumes in "${selectedCategory || 'all categories'}" match your search term. Try a different search.`
                                            : selectedCategory
                                                ? `No resumes are available in the "${selectedCategory}" category.`
                                                : 'No resumes are available right now.'}
                                </p>
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="mt-4 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 text-sm sm:text-base"
                                    >
                                        Clear Search
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 p-8 sm:p-12 text-center">
                        <div className="text-5xl sm:text-7xl mb-3 sm:mb-4">📋</div>
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No Resumes Yet</h3>
                        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">No resumes are available in the library right now.</p>
                        <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 max-w-2xl mx-auto">
                            {categories.slice(0, 8).map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className="px-2 sm:px-4 py-1.5 sm:py-2 bg-blue-50 text-blue-700 font-medium rounded-lg hover:bg-blue-100 transition-colors text-xs sm:text-sm"
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Modals - kept as is */}
                {showAssignModal && assignTargetResume && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-[1px]">
                        <div className="w-full max-w-xl overflow-hidden rounded-[28px] border border-gray-200 bg-white text-gray-900 shadow-2xl">
                            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
                                <div>
                                    <h3 className="text-2xl font-semibold tracking-tight text-gray-900">Assign Resume</h3>
                                    <p className="mt-3 text-sm text-gray-500">
                                        Assign <span className="font-medium text-gray-900">{assignTargetResume.extracted_name || assignTargetResume.user_email || 'this resume'}</span> to recruiters.
                                    </p>
                                    {assignTargetResume.is_hired && (
                                        <p className="mt-2 text-sm font-medium text-emerald-700">
                                            This resume is already hired{assignTargetResume.hired_company_name ? ` for ${assignTargetResume.hired_company_name}` : ''}{assignTargetResume.hired_requirement_name ? ` / ${assignTargetResume.hired_requirement_name}` : ''} and cannot be reassigned.
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        setShowAssignModal(false);
                                        setAssignTargetResume(null);
                                        setSelectedEmployeeIds([]);
                                    }}
                                    className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="space-y-4 px-6 py-5">
                                <div>
                                    <label className="mb-2 block text-base font-medium text-gray-900">
                                        Select Recruiters <span className="text-red-400">*</span>
                                    </label>
                                    <div className="mb-2 flex flex-wrap items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedEmployeeIds(employees.map((employee) => String(employee.id)))}
                                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedEmployeeIds([])}
                                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    <div className="max-h-72 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2">
                                        <div className="space-y-1">
                                            {employees.map((employee) => {
                                                const checked = selectedEmployeeIds.includes(String(employee.id));
                                                return (
                                                    <label
                                                        key={employee.id}
                                                        className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2 text-sm transition hover:bg-blue-50"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(event) => {
                                                                const employeeId = String(employee.id);
                                                                setSelectedEmployeeIds((prev) =>
                                                                    event.target.checked
                                                                        ? [...prev, employeeId]
                                                                        : prev.filter((id) => id !== employeeId)
                                                                );
                                                            }}
                                                            className="mt-1 h-4 w-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <div className="min-w-0">
                                                            <div className="font-medium text-gray-900">{employee.name}</div>
                                                            <div className="text-gray-500">{employee.personnel_id || employee.email}</div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                            {employees.length === 0 ? (
                                                <div className="px-3 py-6 text-center text-sm text-gray-500">
                                                    No active recruiters found.
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <p className="mt-2 text-xs text-gray-500">Selected: {selectedEmployeeIds.length}</p>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-5">
                                <button
                                    onClick={() => {
                                        setShowAssignModal(false);
                                        setAssignTargetResume(null);
                                        setSelectedEmployeeIds([]);
                                    }}
                                    className="rounded-2xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAssignResume}
                                    disabled={assignSaving || assignTargetResume.is_hired}
                                    className="rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                                >
                                    {assignSaving ? 'Saving...' : 'Assign'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showTagModal && tagTargetResume && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-[1px]">
                        <div className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-[28px] border border-gray-200 bg-white text-gray-900 shadow-2xl">
                            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
                                <div>
                                    <h3 className="text-2xl font-semibold tracking-tight text-gray-900">Tag Resume</h3>
                                    <p className="mt-3 text-sm text-gray-500">
                                        Tag <span className="font-medium text-gray-900">{tagTargetResume.extracted_name || tagTargetResume.user_email || 'this resume'}</span> to one or more companies.
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowTagModal(false);
                                        setTagTargetResume(null);
                                        setSelectedTagCompanyIds([]);
                                        setSelectedRequirementIds([]);
                                        setSelectedTagEmployeeIds([]);
                                        setTagNotes('');
                                    }}
                                    className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="space-y-4 overflow-y-auto px-6 py-5">
                                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                                    <div><span className="font-medium text-gray-900">Resume:</span> {tagTargetResume.original_filename}</div>
                                    <div className="mt-1"><span className="font-medium text-gray-900">Current Tags:</span> {tagTargetResume.sub_company_names?.join(', ') || 'No tags'}</div>
                                    {tagTargetResume.requirement_names?.length ? (
                                        <div className="mt-1"><span className="font-medium text-gray-900">Current Requirements:</span> {tagTargetResume.requirement_names.join(', ')}</div>
                                    ) : null}
                                    {tagTargetResume.resume_source_label ? (
                                        <div className="mt-1"><span className="font-medium text-gray-900">Source:</span> {tagTargetResume.resume_source_label}</div>
                                    ) : null}
                                </div>

                                <div>
                                    <label className="mb-2 block text-base font-medium text-gray-900">
                                        Select Companies <span className="text-red-400">*</span>
                                    </label>
                                    <div className="mb-2 flex flex-wrap items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedTagCompanyIds(subCompanies.map((company) => String(company.id)));
                                                setSelectedRequirementIds(
                                                    subCompanies.flatMap((company) =>
                                                        (company.requirements || []).map((requirement) => String(requirement.id))
                                                    )
                                                );
                                            }}
                                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedTagCompanyIds([]);
                                                setSelectedRequirementIds([]);
                                            }}
                                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2">
                                        <div className="space-y-1">
                                            {subCompanies.map((company) => {
                                                const checked = selectedTagCompanyIds.includes(String(company.id));
                                                return (
                                                    <label
                                                        key={company.id}
                                                        className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2 text-sm transition hover:bg-amber-50"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(event) => {
                                                                const companyId = String(company.id);
                                                                setSelectedTagCompanyIds((prev) => {
                                                                    const nextCompanyIds = event.target.checked
                                                                        ? [...prev, companyId]
                                                                        : prev.filter((id) => id !== companyId);

                                                                    if (!event.target.checked) {
                                                                        const companyRequirementIds = (company.requirements || []).map((item) => String(item.id));
                                                                        setSelectedRequirementIds((prevRequirementIds) =>
                                                                            prevRequirementIds.filter((id) => !companyRequirementIds.includes(id))
                                                                        );
                                                                    }

                                                                    return nextCompanyIds;
                                                                });
                                                            }}
                                                            className="mt-1 h-4 w-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <div className="min-w-0">
                                                            <div className="font-medium text-gray-900">{company.name}</div>
                                                            <div className="text-gray-500">{company.contact_email || company.contact_name || 'Sub company'}</div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                            {subCompanies.length === 0 ? (
                                                <div className="px-3 py-6 text-center text-sm text-gray-500">
                                                    No sub companies found.
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <p className="mt-2 text-xs text-gray-500">Selected: {selectedTagCompanyIds.length}</p>
                                </div>

                                <div>
                                    <label className="mb-2 block text-base font-medium text-gray-900">
                                        Select Requirements
                                    </label>
                                    <div className="mb-2 flex flex-wrap items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setSelectedRequirementIds(
                                                    getRequirementOptionsForSelectedCompanies().map((requirement) => String(requirement.id))
                                                )
                                            }
                                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedRequirementIds([])}
                                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    <div className="max-h-44 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2">
                                        <div className="space-y-1">
                                            {getRequirementOptionsForSelectedCompanies().map((requirement) => {
                                                const requirementId = String(requirement.id);
                                                const checked = selectedRequirementIds.includes(requirementId);
                                                return (
                                                    <label
                                                        key={requirementId}
                                                        className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2 text-sm transition hover:bg-blue-50"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(event) => {
                                                                setSelectedRequirementIds((prev) =>
                                                                    event.target.checked
                                                                        ? [...prev, requirementId]
                                                                        : prev.filter((id) => id !== requirementId)
                                                                );
                                                            }}
                                                            className="mt-1 h-4 w-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <div className="min-w-0">
                                                            <div className="font-medium text-gray-900">{requirement.name}</div>
                                                            <div className="text-gray-500">{requirement.sub_company_name}</div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                            {getRequirementOptionsForSelectedCompanies().length === 0 ? (
                                                <div className="px-3 py-6 text-center text-sm text-gray-500">
                                                    Select a company first to choose its requirements.
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <p className="mt-2 text-xs text-gray-500">Selected: {selectedRequirementIds.length}</p>
                                </div>

                                <div>
                                    <label className="mb-2 block text-sm font-medium text-gray-900">Requirement Notes</label>
                                    <textarea
                                        rows={3}
                                        value={tagNotes}
                                        onChange={(event) => setTagNotes(event.target.value)}
                                        className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                                        placeholder="Optional notes for the tagged companies"
                                    />
                                </div>

                                <div>
                                    <label className="mb-2 block text-base font-medium text-gray-900">
                                        Assign Recruiters
                                    </label>
                                    <div className="mb-2 flex flex-wrap items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedTagEmployeeIds(employees.map((employee) => String(employee.id)))}
                                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedTagEmployeeIds([])}
                                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2">
                                        <div className="space-y-1">
                                            {employees.map((employee) => {
                                                const checked = selectedTagEmployeeIds.includes(String(employee.id));
                                                return (
                                                    <label
                                                        key={`tag-employee-${employee.id}`}
                                                        className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2 text-sm transition hover:bg-cyan-50"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={(event) => {
                                                                const employeeId = String(employee.id);
                                                                setSelectedTagEmployeeIds((prev) =>
                                                                    event.target.checked
                                                                        ? [...prev, employeeId]
                                                                        : prev.filter((id) => id !== employeeId)
                                                                );
                                                            }}
                                                            className="mt-1 h-4 w-4 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <div className="min-w-0">
                                                            <div className="font-medium text-gray-900">{employee.name}</div>
                                                            <div className="text-gray-500">{employee.personnel_id || employee.email}</div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                            {employees.length === 0 ? (
                                                <div className="px-3 py-6 text-center text-sm text-gray-500">
                                                    No active recruiters found.
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <p className="mt-2 text-xs text-gray-500">Selected: {selectedTagEmployeeIds.length}</p>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-5">
                                <button
                                    onClick={() => {
                                        setShowTagModal(false);
                                        setTagTargetResume(null);
                                        setSelectedTagCompanyIds([]);
                                        setSelectedRequirementIds([]);
                                        setSelectedTagEmployeeIds([]);
                                        setTagNotes('');
                                    }}
                                    className="rounded-2xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveTags}
                                    disabled={tagSaving}
                                    className="rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                                >
                                    {tagSaving ? 'Saving...' : 'Save Tags'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Resume Details Modal */}
                {showResumeModal && selectedResume && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
                        <div className="bg-white rounded-lg sm:rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
                                <h3 className="text-base sm:text-lg font-bold text-gray-900">Resume Details</h3>
                                <button
                                    onClick={() => setShowResumeModal(false)}
                                    className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                                <div className="flex items-center gap-3 pb-3 sm:pb-4 border-b border-gray-200">
                                    <span className="text-2xl sm:text-3xl">{getFileIcon(selectedResume.original_filename)}</span>
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 break-words">{selectedResume.original_filename}</h2>
                                        <p className="text-xs sm:text-sm text-gray-500">
                                            Uploaded by: {selectedResume.uploaded_by_name || '-'}
                                            {selectedResume.uploaded_by_display_id ? ` (${selectedResume.uploaded_by_display_id})` : ''}
                                        </p>
                                        <p className="text-xs sm:text-sm text-gray-500">
                                            Uploaded: {formatKolkataDateTime(selectedResume.upload_date)}
                                            {selectedResume.file_size && ` • ${(selectedResume.file_size / 1024).toFixed(2)} KB`}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Candidate Summary</h4>
                                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                                        <p className="text-sm sm:text-base"><span className="text-gray-600">Name:</span> {getCandidateName(selectedResume)}</p>
                                        <p className="text-sm sm:text-base break-words"><span className="text-gray-600">Email:</span> {selectedResume.extracted_email || selectedResume.extracted_data?.email || 'N/A'}</p>
                                        <p className="text-sm sm:text-base"><span className="text-gray-600">Phone:</span> {selectedResume.extracted_phone || selectedResume.extracted_data?.phone || 'N/A'}</p>
                                        <p className="text-sm sm:text-base"><span className="text-gray-600">Job Title:</span> {selectedResume.extracted_job_title || selectedResume.extracted_data?.job_title || 'N/A'}</p>
                                    </div>
                                </div>

                                {(selectedResume.sub_company_names?.length || selectedResume.sub_company_name || selectedResume.requirement_names?.length || selectedResume.requirement_notes) && (
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Tagged Company</h4>
                                        <div className="bg-indigo-50 rounded-lg p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                                            <p className="text-sm sm:text-base"><span className="text-gray-600">Company:</span> {selectedResume.sub_company_names?.join(", ") || selectedResume.sub_company_name || '-'}</p>
                                            {selectedResume.requirement_names?.length ? (
                                                <p className="text-sm sm:text-base"><span className="text-gray-600">Requirements:</span> {selectedResume.requirement_names.join(", ")}</p>
                                            ) : null}
                                            {selectedResume.requirement_notes && (
                                                <p className="text-sm sm:text-base"><span className="text-gray-600">Requirement Notes:</span> {selectedResume.requirement_notes}</p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {selectedResume.is_hired && (
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Selection Details</h4>
                                        <div className="bg-emerald-50 rounded-lg p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                                            <p className="text-sm sm:text-base"><span className="text-gray-600">Current Status:</span> Hired</p>
                                            <p className="text-sm sm:text-base"><span className="text-gray-600">Selected Company:</span> {selectedResume.hired_company_name || '-'}</p>
                                            <p className="text-sm sm:text-base"><span className="text-gray-600">Selected Requirement:</span> {selectedResume.hired_requirement_name || '-'}</p>
                                            <p className="text-sm sm:text-base"><span className="text-gray-600">Handled By Recruiter:</span> {selectedResume.hired_employee_name || '-'}</p>
                                            {selectedResume.hired_amount ? (
                                                <p className="text-sm sm:text-base"><span className="text-gray-600">Hired Amount:</span> {formatHiredAmount(selectedResume.hired_amount, selectedResume.hired_amount_unit)}</p>
                                            ) : null}
                                        </div>
                                    </div>
                                )}

                                {selectedResume.extracted_data && (
                                    <>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Personal Information</h4>
                                            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                                                {(selectedResume.extracted_data.full_name || selectedResume.extracted_name || inferNameFromFilename(selectedResume.original_filename)) && (
                                                    <p className="text-sm sm:text-base"><span className="text-gray-600">Name:</span> {getCandidateName(selectedResume)}</p>
                                                )}
                                                {(selectedResume.extracted_data.email || selectedResume.extracted_email) && (
                                                    <p className="text-sm sm:text-base break-words"><span className="text-gray-600">Email:</span> {selectedResume.extracted_data.email || selectedResume.extracted_email}</p>
                                                )}
                                                {(selectedResume.extracted_data.phone || selectedResume.extracted_phone) && (
                                                    <p className="text-sm sm:text-base"><span className="text-gray-600">Phone:</span> {selectedResume.extracted_data.phone || selectedResume.extracted_phone}</p>
                                                )}
                                                {getCleanLocation(selectedResume) && (
                                                    <p className="text-sm sm:text-base"><span className="text-gray-600">Location:</span> {getCleanLocation(selectedResume)}</p>
                                                )}
                                            </div>
                                        </div>

                                        {(selectedResume.extracted_data.job_title || selectedResume.extracted_job_title || selectedResume.extracted_data.category || selectedResume.extracted_data.experience_years > 0) && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Professional Information</h4>
                                            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-1.5 sm:space-y-2">
                                                {(selectedResume.extracted_data.job_title || selectedResume.extracted_job_title) && (
                                                    <p className="text-sm sm:text-base"><span className="text-gray-600">Job Title:</span> {selectedResume.extracted_data.job_title || selectedResume.extracted_job_title}</p>
                                                    )}
                                                    {selectedResume.extracted_data.category && (
                                                        <p className="text-sm sm:text-base"><span className="text-gray-600">Category:</span> {selectedResume.extracted_data.category}</p>
                                                    )}
                                                    {getResumeExperienceYears(selectedResume) > 0 && (
                                                        <p className="text-sm sm:text-base"><span className="text-gray-600">Experience:</span> {getResumeExperienceYears(selectedResume)} years</p>
                                                    )}
                                                    {(selectedResume.ats_score > 0 || selectedResume.extracted_data.ats_score > 0) && (
                                                        <p className="text-sm sm:text-base"><span className="text-gray-600">ATS Score:</span> {selectedResume.ats_score || selectedResume.extracted_data.ats_score}%</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {selectedResume.extracted_data.skills?.length > 0 && (
                                            <div>
                                                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Skills</h4>
                                                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                                    {selectedResume.extracted_data.skills.map((skill, idx) => (
                                                        <span key={idx} className="px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-50 text-blue-700 text-xs sm:text-sm rounded-lg">
                                                            {skill}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {selectedResume.extracted_data.experiences?.length > 0 && (
                                            <div>
                                                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Work Experience</h4>
                                                <div className="space-y-2 sm:space-y-3">
                                                    {selectedResume.extracted_data.experiences.map((exp, idx) => (
                                                        <div key={idx} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                                                            {typeof exp === 'string' ? (
                                                                <div>
                                                                    <p className="text-gray-900 whitespace-pre-wrap text-sm sm:text-base">{exp}</p>
                                                                    <p className="text-xs text-gray-500 mt-1.5 sm:mt-2">Experience #{idx + 1}</p>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <p className="font-medium text-gray-900 text-sm sm:text-base">{exp.title || exp.position || 'Position'}</p>
                                                                    {exp.company && <p className="text-xs sm:text-sm text-gray-600">{exp.company}</p>}
                                                                    {(exp.start_date || exp.end_date) && (
                                                                        <p className="text-xs text-gray-500 mt-1">
                                                                            {exp.start_date} - {exp.end_date || 'Present'}
                                                                        </p>
                                                                    )}
                                                                    {exp.description && (
                                                                        <p className="text-xs sm:text-sm text-gray-600 mt-1.5 sm:mt-2">{exp.description}</p>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {selectedResume.extracted_data.education?.length > 0 && (
                                            <div>
                                                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Education</h4>
                                                <div className="space-y-2 sm:space-y-3">
                                                    {selectedResume.extracted_data.education.map((edu, idx) => (
                                                        <div key={idx} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                                                            {typeof edu === 'string' ? (
                                                                <div>
                                                                    <p className="text-gray-900 whitespace-pre-wrap text-sm sm:text-base">{edu}</p>
                                                                    <p className="text-xs text-gray-500 mt-1.5 sm:mt-2">Education #{idx + 1}</p>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <p className="font-medium text-gray-900 text-sm sm:text-base">{edu.degree || edu.course || 'Degree'}</p>
                                                                    {edu.institution && <p className="text-xs sm:text-sm text-gray-600">{edu.institution}</p>}
                                                                    {edu.year && <p className="text-xs text-gray-500 mt-1">{edu.year}</p>}
                                                                    {edu.percentage && <p className="text-xs text-gray-500">Percentage: {edu.percentage}</p>}
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {selectedResume.extracted_data.certifications?.length > 0 && (
                                            <div>
                                                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Certifications</h4>
                                                <div className="space-y-2 sm:space-y-3">
                                                    {selectedResume.extracted_data.certifications.map((cert, idx) => (
                                                        <div key={idx} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                                                            {typeof cert === 'string' ? (
                                                                <p className="text-gray-900 whitespace-pre-wrap text-sm sm:text-base">{cert}</p>
                                                            ) : (
                                                                <>
                                                                    <p className="font-medium text-gray-900 text-sm sm:text-base">{cert.name || cert.title}</p>
                                                                    {cert.issuer && <p className="text-xs sm:text-sm text-gray-600">{cert.issuer}</p>}
                                                                    {cert.year && <p className="text-xs text-gray-500">{cert.year}</p>}
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {selectedResume.extracted_data.summary && (
                                            <div>
                                                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Summary</h4>
                                                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                                                    <p className="text-gray-900 whitespace-pre-wrap text-sm sm:text-base">{selectedResume.extracted_data.summary}</p>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-gray-200">
                                    <button
                                        onClick={() => handleViewResume(selectedResume)}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 text-sm sm:text-base"
                                    >
                                        View Resume
                                    </button>
                                    <button
                                        onClick={() => handleDownloadResume(selectedResume)}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 text-sm sm:text-base"
                                    >
                                        Download
                                    </button>
                                    <button
                                        onClick={() => setShowResumeModal(false)}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 text-sm sm:text-base"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </CompanyLayout>
    );
};

export default Dashboard;
